import type { AxiosRequestConfigExtended } from '@vision/ui/interfaces';
import type {
  PlannerTasksArgs,
  PlannerTaskCreateArgs,
  PlannerTaskUpdateArgs,
  PlannerTaskAttachmentDeleteArgs,
  PlannerTaskAttachmentCreateArgs,
  PlannerTaskSearchArgs,
  PlannerTaskDetailArgs,
} from './tasks.api.types';
import { createHttpExtraOptions } from '@vision/ui/utils';

export const buildGetDashboardTasksRequest = ({
  accountId,
  plannerId,
  dashboardId,
  query,
}: PlannerTasksArgs): AxiosRequestConfigExtended => ({
  url: `/v2/planners/${plannerId}/tasks${query ? `?${query}` : ''}`,
  method: 'GET',
  params: { account_id: accountId, dashboard_id: dashboardId },
  extraOptions: createHttpExtraOptions({
    notification: {
      autoHandleError: false,
    },
  }),
});

export const buildCreateTaskRequest = ({
  accountId,
  plannerId,
  dashboardId,
  data,
  uploadTrackId,
}: PlannerTaskCreateArgs): AxiosRequestConfigExtended => {
  const formData = new FormData();

  Object.entries(data)
    .filter(([key]) => key !== 'attachments' && key !== 'task_relations')
    .forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((item) => formData.append(`${key}[]`, typeof item === 'object' ? JSON.stringify(item) : item));
        } else {
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
        }
      }
    });

  if (data.attachments?.length) {
    data.attachments.forEach((attachment) => formData.append('attachments[]', attachment.file));
  }

  if (data.task_relations?.length) {
    data.task_relations.forEach((taskRelation) => {
      formData.append('relations[][record_id]', taskRelation.record_id);
      formData.append('relations[][record_type]', taskRelation.record_type);
      if (taskRelation.detail?.tile_id) {
        formData.append('relations[][detail][tile_id]', taskRelation.detail.tile_id);
      }
    });
  }

  return {
    url: `/v2/planners/${plannerId}/tasks`,
    method: 'POST',
    params: { account_id: accountId, dashboard_id: dashboardId },
    data: formData,
    uploadTrackId,
    fileFieldName: 'attachments',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    extraOptions: createHttpExtraOptions({
      notification: {
        translationMessageKey: 'http.message.planner.createTask.{{status}}',
      },
    }),
  };
};

export const buildUpdateTaskRequest = ({
  accountId,
  plannerId,
  dashboardId,
  taskId,
  data,
}: PlannerTaskUpdateArgs): AxiosRequestConfigExtended => {
  const payload = { ...data };
  delete payload.attachments;

  return {
    url: `/v2/planners/${plannerId}/tasks/${taskId}`,
    method: 'POST',
    params: { account_id: accountId, dashboard_id: dashboardId },
    data: payload,
    extraOptions: createHttpExtraOptions({
      notification: {
        translationMessageKey: 'http.message.planner.updateTask.{{status}}',
      },
    }),
  };
};

export const buildDeleteAttachmentRequest = ({
  accountId,
  plannerId,
  dashboardId,
  taskId,
  blobId,
}: PlannerTaskAttachmentDeleteArgs): AxiosRequestConfigExtended => ({
  url: `/v2/planners/${plannerId}/attachments/${blobId}/delete`,
  method: 'POST',
  params: {
    account_id: accountId,
    dashboard_id: dashboardId,
    task_id: taskId,
  },
  extraOptions: createHttpExtraOptions({
    notification: {
      translationMessageKey: 'http.message.planner.deleteAttachment.{{status}}',
    },
  }),
});

export const buildCreateTaskAttachmentRequest = ({
  accountId,
  plannerId,
  dashboardId,
  taskId,
  attachments,
  uploadTrackId,
}: PlannerTaskAttachmentCreateArgs): AxiosRequestConfigExtended => {
  const formData = new FormData();
  attachments.forEach((file) => formData.append('attachments[]', file));

  return {
    url: `/v2/planners/${plannerId}/attachments`,
    method: 'POST',
    params: { account_id: accountId, dashboard_id: dashboardId, task_id: taskId },
    data: formData,
    uploadTrackId,
    fileFieldName: 'attachments',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    extraOptions: createHttpExtraOptions({
      notification: {
        translationMessageKey: 'http.message.planner.createTaskAttachment.{{status}}',
      },
    }),
  };
};

export const buildSearchTasksRequest = ({
  plannerId,
  accountId,
  query,
}: PlannerTaskSearchArgs): AxiosRequestConfigExtended => ({
  url: `/v2/planners/${plannerId}/search${query ? `?${query}` : ''}`,
  method: 'GET',
  params: {
    account_id: accountId,
  },
});

export const buildShowTaskRequest = ({
  plannerId,
  taskId,
  accountId,
  dashboardId,
}: PlannerTaskDetailArgs): AxiosRequestConfigExtended => ({
  url: `/v2/planners/${plannerId}/tasks/${taskId}`,
  method: 'GET',
  params: { account_id: accountId, dashboard_id: dashboardId },
});
